import { Inject, Injectable } from '@angular/core';
import {
  FunctionBlockConfigEvent,
  FunctionBlockFunctionResponseEvent,
  FunctionBlockLogResultEvent,
  FunctionBlockMessage,
  FunctionBlockStateEvent,
  NetworkDeviceActivationMessageEvent,
  NetworkDeviceMessageEvent,
  NetworkDeviceStateEvent,
} from '@ubie/mqttcommunication';
import { Observable, ReplaySubject, Subject, merge } from 'rxjs';
import { FunctionBlockCommunicationResult } from '../../../classes/FunctionBlockCommunicationResult';
import { NetworkDeviceCommunicationResult } from '../../../classes/NetworkDeviceCommunicationResult';
import { UbieDeviceCommunicationResult } from '../../../classes/UbieDeviceCommunicationResult';
import { UbieState } from '../../../interfaces/index';
import { LogService } from '../../log/log.service';

@Injectable({
  providedIn: 'root',
})
export class CommunicationListenerService {
  private functionBlockStateListeners: {
    [key: string]: Subject<FunctionBlockStateEvent>;
  } = {};
  private functionBlockConfigListeners: {
    [key: string]: Subject<FunctionBlockConfigEvent>;
  } = {};
  private networkDeviceListeners: {
    [key: string]: Subject<NetworkDeviceStateEvent>;
  } = {};
  private deviceListeners: {
    [key: string]: Subject<UbieState>;
  } = {};
  private graphListeners: {
    [key: string]: Subject<FunctionBlockLogResultEvent>;
  } = {};

  private fbMessageListeners: {
    [key: string]: Subject<FunctionBlockMessage>;
  } = {};

  private readonly fbFunctionBlockFunctionListener: ReplaySubject<FunctionBlockFunctionResponseEvent> =
    new ReplaySubject<FunctionBlockFunctionResponseEvent>(1, 10000, Date);

  private readonly networkDeviceMessageListener: Subject<NetworkDeviceMessageEvent> =
    new Subject<NetworkDeviceMessageEvent>();

  private readonly networkDeviceActivationMessageListener: Subject<NetworkDeviceActivationMessageEvent> =
    new Subject<NetworkDeviceActivationMessageEvent>();

  private readonly logService: LogService = Inject(LogService);

  getFunctionBlockCommunication(
    ubieId: string,
    networkDeviceId: string,
    functionBlockId: string,
  ): FunctionBlockCommunicationResult {
    const fbuid = `${ubieId}/${networkDeviceId}/${functionBlockId}`;
    const ndUid = `${ubieId}/${networkDeviceId}`;

    const configListener = this.getFunctionBlockConfigListener(fbuid);
    const stateListener = this.getFunctionBlockStateListener(fbuid);
    const logListener = this.getGraphListener(fbuid);

    const fbMessageListener = this.getFbMessageListener(fbuid);

    const networkDeviceListener = this.getNetworkDeviceListener(ndUid);
    const deviceListener = this.getDeviceListener(ubieId);

    return new FunctionBlockCommunicationResult(
      deviceListener,
      networkDeviceListener,
      stateListener,
      configListener,
      logListener,
      fbMessageListener,
    );
  }

  getFunctionBlockCommunicationOnly(
    ubieId: string,
    networkDeviceId: string,
    functionBlockId: string,
  ): Observable<FunctionBlockStateEvent | FunctionBlockConfigEvent> {
    const fbuid = `${ubieId}/${networkDeviceId}/${functionBlockId}`;
    const configListener = this.getFunctionBlockConfigListener(fbuid);
    const stateListener = this.getFunctionBlockStateListener(fbuid);

    return merge(stateListener.asObservable(), configListener.asObservable());
  }

  getNetworkDeviceCommunication(
    ubieId: string,
    networkDeviceId: string,
  ): NetworkDeviceCommunicationResult {
    const ndUid = `${ubieId}/${networkDeviceId}`;

    const networkDeviceListener = this.getNetworkDeviceListener(ndUid);
    const deviceListener = this.getDeviceListener(ubieId);

    return new NetworkDeviceCommunicationResult(
      deviceListener,
      networkDeviceListener,
    );
  }

  getNetworkDeviceCommunicationOnly(
    ubieId: string,
    networkDeviceId: string,
  ): Observable<NetworkDeviceStateEvent> {
    const ndUid = `${ubieId}/${networkDeviceId}`;

    const networkDeviceListener = this.getNetworkDeviceListener(ndUid);

    return networkDeviceListener.asObservable();
  }

  getUbieDeviceCommunication(ubieId: string): UbieDeviceCommunicationResult {
    const deviceListener = this.getDeviceListener(ubieId);
    return new UbieDeviceCommunicationResult(deviceListener);
  }

  getFunctionBlockFunctionListener(): Observable<FunctionBlockFunctionResponseEvent> {
    return this.fbFunctionBlockFunctionListener.asObservable();
  }

  getNetworkDeviceMessageListener(): Observable<NetworkDeviceMessageEvent> {
    return this.networkDeviceMessageListener.asObservable();
  }

  getFunctionBlockMessageListener(
    ubieId: string,
    networkDeviceId: string,
    functionBlockId: string,
  ): Observable<FunctionBlockMessage> {
    const fbuid = `${ubieId}/${networkDeviceId}/${functionBlockId}`;
    const fbMessageListener = this.getFbMessageListener(fbuid);
    return fbMessageListener.asObservable();
  }

  getNetworkDeviceActivationMessageListener(): Observable<NetworkDeviceActivationMessageEvent> {
    return this.networkDeviceActivationMessageListener.asObservable();
  }
  //#region setters

  setUbieDeviceState(ubieId: string, state: UbieState): void {
    const deviceListener = this.getDeviceListener(ubieId);
    deviceListener.next(state);
  }

  /**
   * sets a networkdevice state on the subject
   * @param ubieId the id of the ubie
   * @param networkDeviceId the id of the network device
   * @param state the state to set
   */
  setNetworkDeviceState(
    ubieId: string,
    networkDeviceId: string,
    state: NetworkDeviceStateEvent,
  ): void {
    const ndUid = `${ubieId}/${networkDeviceId}`;
    const networkDeviceListener = this.getNetworkDeviceListener(ndUid);
    networkDeviceListener.next(state);
  }

  setNetworkDeviceMessage(message: NetworkDeviceMessageEvent): void {
    this.networkDeviceMessageListener.next(message);
  }

  setNetworkDeviceActivationMessage(
    message: NetworkDeviceActivationMessageEvent,
  ): void {
    this.networkDeviceActivationMessageListener.next(message);
  }

  /**
   * sets a functionblock state on the subject
   * @param ubieId the id of the ubie
   * @param networkDeviceId the id of the network device
   * @param functionBlockId the id of the function block
   * @param state the state to set
   */
  setFunctionBlockState(
    ubieId: string,
    networkDeviceId: string,
    functionBlockId: string,
    state: FunctionBlockStateEvent,
  ): void {
    const fbuid = `${ubieId}/${networkDeviceId}/${functionBlockId}`;
    const stateListener = this.getFunctionBlockStateListener(fbuid);
    stateListener.next(state);
  }

  /**
   * sets a functionblock config on the subject
   * @param ubieId the id of the ubie
   * @param networkDeviceId the id of the network device
   * @param functionBlockId the id of the function block
   * @param config the config to set
   */
  setFunctionBlockConfig(
    ubieId: string,
    networkDeviceId: string,
    functionBlockId: string,
    config: FunctionBlockConfigEvent,
  ): void {
    const fbuid = `${ubieId}/${networkDeviceId}/${functionBlockId}`;
    const configListener = this.getFunctionBlockConfigListener(fbuid);
    configListener.next(config);
  }

  setLogResult(
    ubieId: string,
    networkDeviceId: string,
    functionBlockId: string,
    result: FunctionBlockLogResultEvent,
  ): void {
    const fbuid = `${ubieId}/${networkDeviceId}/${functionBlockId}`;
    const graphListener = this.getGraphListener(fbuid);
    graphListener.next(result);
  }

  setFunctionBlockFunctionResponse(
    result: FunctionBlockFunctionResponseEvent,
  ): void {
    this.fbFunctionBlockFunctionListener.next(result);
  }

  setFunctionBlockMessage(
    ubieId: string,
    networkDeviceId: string,
    functionBlockId: string,
    message: string,
  ): void {
    try {
      const fbuid = `${ubieId}/${networkDeviceId}/${functionBlockId}`;
      const fbMessageListener = this.getFbMessageListener(fbuid);
      fbMessageListener.next(JSON.parse(message) as FunctionBlockMessage);
    } catch (e) {
      this.logService.error('error in parsing functionblock message', e);
    }
  }

  //#endregion

  //#region private getters

  private getFunctionBlockStateListener(
    uid: string,
  ): Subject<FunctionBlockStateEvent> {
    let fbStateSub = this.functionBlockStateListeners[uid];
    if (!fbStateSub) {
      fbStateSub = new Subject();
      this.functionBlockStateListeners[uid] = fbStateSub;
    }
    return fbStateSub;
  }

  private getFbMessageListener(uid: string): Subject<any> {
    let fbMessageSub = this.fbMessageListeners[uid];
    if (!fbMessageSub) {
      fbMessageSub = new Subject();
      this.fbMessageListeners[uid] = fbMessageSub;
    }
    return fbMessageSub;
  }

  private getFunctionBlockConfigListener(
    uid: string,
  ): Subject<FunctionBlockConfigEvent> {
    let fbConfigSub = this.functionBlockConfigListeners[uid];
    if (!fbConfigSub) {
      fbConfigSub = new Subject();
      this.functionBlockConfigListeners[uid] = fbConfigSub;
    }
    return fbConfigSub;
  }

  private getGraphListener(
    fbUid: string,
  ): Subject<FunctionBlockLogResultEvent> {
    let sub = this.graphListeners[fbUid];

    if (!sub) {
      sub = new Subject();
      this.graphListeners[fbUid] = sub;
    }
    return sub;
  }

  /**
   *
   * @param uid the id of the network device
   * @returns a subject for the states
   */
  private getNetworkDeviceListener(
    uid: string,
  ): Subject<NetworkDeviceStateEvent> {
    let networkDeviceSub: Subject<NetworkDeviceStateEvent> =
      this.networkDeviceListeners[uid];

    if (!networkDeviceSub) {
      networkDeviceSub = new Subject<NetworkDeviceStateEvent>();
      this.networkDeviceListeners[uid] = networkDeviceSub;
    }
    return networkDeviceSub;
  }

  /**
   * gets the communication for a ubie device
   * @param uid ubie id
   * @returns object with the observables to listen to ubie state
   */
  private getDeviceListener(uid: string): Subject<UbieState> {
    let ubieSub: Subject<UbieState> = this.deviceListeners[uid];

    if (!ubieSub) {
      ubieSub = new Subject<UbieState>();
      this.deviceListeners[uid] = ubieSub;
    }
    return ubieSub;
  }

  //#endregion

  removeAll(): void {
    this.deviceListeners = {};
    this.networkDeviceListeners = {};
    this.functionBlockStateListeners = {};
    this.functionBlockConfigListeners = {};
    this.graphListeners = {};
  }

  removeListener(uid: string): void {
    try {
      if (this.deviceListeners[uid]) {
        this.deviceListeners[uid].complete();
        delete this.deviceListeners[uid];
      }

      if (this.networkDeviceListeners[uid]) {
        this.networkDeviceListeners[uid].complete();
        delete this.networkDeviceListeners[uid];
      }

      if (this.functionBlockStateListeners[uid]) {
        this.functionBlockStateListeners[uid].complete();
        delete this.functionBlockStateListeners[uid];
      }

      if (this.functionBlockConfigListeners[uid]) {
        this.functionBlockConfigListeners[uid].complete();
        delete this.functionBlockConfigListeners[uid];
      }

      if (this.graphListeners[uid]) {
        this.graphListeners[uid].complete();
        delete this.graphListeners[uid];
      }
    } catch (e) {
      this.logService.error('error in removing listener', e);
    }
  }
}
